import { Inject, Injectable, Optional } from '@angular/core';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';
import { isBrowser, readLocalStorage } from 'app/core/utils/platform.util';
import { BehaviorSubject, defer, EMPTY, from, Observable, of, retry, Subject } from 'rxjs';
import { catchError, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { NetworkService } from './network.service';
import { UtilService } from './util.service';
import {
    categoryPathMatchesQuerySlug,
    categoryPathToQueryValueWithFormat,
    CategoryQueryFormat,
    detectCategoryQueryFormat,
    normalizeCategoryPath,
    normalizeSingleSegmentPath,
    parseHyphenJoinedCategorySlug,
    preserveCategoryPathSegments,
    toApiCategoryPath,
} from 'app/core/utils/category-path.util';
import { buildLegacyMissingKbSlugRedirectPath } from 'app/core/utils/kb-portal-path.util';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';

declare global {
    interface Window {
        lc_id: any;
        lc_dc: any;
    }
}

interface KbLanguage {
    code: string;
    name: string;
}

interface KbSlugDetails {
    default_kb_id: number;
    default_kb_slug: string;
    default_language: string;
    default_other_languages: KbLanguage[];
}

@Injectable({
    providedIn: 'root',
})
export class KbService {
    kbSettings: any = {};
    private kbSlugDetailsSubject = new BehaviorSubject<KbSlugDetails | null>(null);
    readonly kbSlugDetails$ = this.kbSlugDetailsSubject.asObservable();
    private kbSlugDetailsRequest$: Observable<KbSlugDetails | null> | null = null;
    private darkerColorCache = new Map<string, string>();
    private textColorCache = new Map<string, string>();
    private collectionStylesCache = new Map<string, Record<string, string> | undefined>();
    isSaving: boolean = false;
    articleCategory: any[] = [];
    isLoadKbSetting = new BehaviorSubject<boolean>(false);
    actCategory: BehaviorSubject<number>;
    activeCategory$: Observable<number>;
    cs_img_img: any = '';
    allArticles: any[] = [];
    articlesSection: any[] = [];
    selectedArticles: any[] = [];
    is_show_collections: boolean = false;
    is_show_articles: boolean = false;
    is_show_contents: boolean = false;
    categoryQueryParamKey = 'category';
    searchUuid: string | null = null;
    gradientPositions: any[] = [
        {
            value: 'to bottom',
            label: 'KB_SETTINGS.TOP_BOTTOM',
        },
        {
            value: 'to top',
            label: 'KB_SETTINGS.BOTTOM_TOP',
        },
        {
            value: 'to right',
            label: 'KB_SETTINGS.LEFT_RIGHT',
        },
        {
            value: 'to left',
            label: 'KB_SETTINGS.RIGHT_LEFT',
        },
    ];
    is_show_manage_links: boolean = false;
    socialMedia: any[] = [
        {
            label: 'Facebook',
            lang: 'Facebook',
            icon: 'ri-facebook-circle-fill',
            link: '',
        },
        {
            label: 'Twitter',
            lang: 'Twitter',
            icon: 'ri-twitter-x-line',
            link: 'https://www.twitter.com/',
        },
        {
            label: 'Instagram',
            lang: 'Instagram',
            icon: 'ri-instagram-line',
            link: 'https://www.instagram.com/',
        },
        {
            label: 'LinkedIn',
            lang: 'LinkedIn',
            icon: 'ri-linkedin-fill',
            link: 'https://www.linkedin.com/',
        },
        {
            label: 'Youtube',
            lang: 'Youtube',
            icon: 'ri-youtube-fill',
            link: 'https://www.youtube.com/',
        },
        {
            label: 'Pinterest',
            lang: 'Pinterest',
            icon: 'ri-pinterest-fill',
            link: 'https://www.pinterest.com/',
        },
    ];
    columns: any[] = [
        {
            column_name: '',
            expanded: true,
            links: [
                {
                    title: '',
                    url: '',
                },
            ],
        },
    ];
    socialMediaLink: any[] = [];
    expandedLabel: string = 'shared_elements';
    isCategoryLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        false
    );
    isArticlesLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        false
    );
    hero_img: any;
    cs_icon_img: any;
    cs_img: any;
    contents_img: any;
    images: any = {};
    activeTheme: string = 'Homepage';
    custActiveCategory: any;
    custArticleCat: any[] = [];
    featured_articles: any[] = [];
    errorData = new BehaviorSubject<any>({});
    attechFile: any;
    currentCategory: any = {};
    /** Active category payload on customer-portal category pages (hero + body). */
    portalCategory: any = null;
    lastNavigatedCategoryPath: string[] | null = null;
    categoryQueryFormat: CategoryQueryFormat = 'slash';
    currentIconCategory: any = {};
    activeCategory: any = 'All categories';
    allCategoriesRequest$: Observable<any>;
    allCategoriesCachedKbId: number;
    tenantArticlesRequest$: Observable<any>;
    tenantArticlesCachedRecord: any;
    tenantArticlesCachedCategoryId: any;
    articlesExplorerRefreshSubject = new Subject<void>();
    articlesExplorerRefresh$ = this.articlesExplorerRefreshSubject.asObservable();
    private activeKnowledgeBaseSubject = new BehaviorSubject<any>(null);
    readonly activeKnowledgeBase$ = this.activeKnowledgeBaseSubject.asObservable();
    private settingsLoadCancel$ = new Subject<void>();
    private articlesCategoryLoadCancel$ = new Subject<void>();
    private articlesLoadCancel$ = new Subject<void>();
    private getImageTimerId: ReturnType<typeof setTimeout> | null = null;
    private portalSsrHydratedSlug: string | null = null;

    constructor(
        private _networkService: NetworkService,
        private _util: UtilService,
        private _api: ApiService,
        private _router: Router,
        @Optional() @Inject(REQUEST) private _request: Request | null,
    ) {
        this.actCategory = new BehaviorSubject<number>(null);
        this.activeCategory$ = this.actCategory.asObservable();
    }

    resetArticlesExplorerSession() {
        this.invalidateAllCategoriesCache();
    }

    setActiveKnowledgeBase(knowledgeBase: any): void {
        this.activeKnowledgeBaseSubject.next(knowledgeBase);
    }

    notifyArticlesExplorerRefresh() {
        this.resetArticlesExplorerSession();
        this.articlesExplorerRefreshSubject.next();
    }

    getActiveKnowledgeBase(): any {
        return this.activeKnowledgeBaseSubject.getValue();
    }

    getActiveKnowledgeBaseId() {
        return this.getActiveKnowledgeBase()?.id || null;
    }

    getActiveCategory() {
        return this.actCategory.getValue();
    }

    getPortalContext(): { kb_slug: string; kb_lang: string } {
        let node = this._router.routerState.snapshot.root;
        let kb_slug: string | null = null;
        let kb_lang: string | null = null;

        while (node) {
            kb_slug = node.paramMap.get('slug') || kb_slug;
            kb_lang = node.paramMap.get('lang') || kb_lang;
            node = node.firstChild;
        }

        return {
            kb_slug: kb_slug || this.kbSettings?.kb_slug || '',
            kb_lang: kb_lang || this.kbSettings?.language || 'en',
        };
    }

    decodeQueryParam(value: string) {
        let decoded = value;
        let prev = '';
        while (decoded !== prev) {
            prev = decoded;
            try {
                decoded = decodeURIComponent(decoded);
            } catch {
                break;
            }
        }
        return decoded;
    }

    getCategoryPath(queryParam: string | null): string[] | null {
        if (!queryParam) return null;
        const decoded = this.decodeQueryParam(queryParam);

        if (decoded.includes('/')) {
            return decoded.split('/').filter(Boolean);
        }

        return decoded ? [decoded] : null;
    }

    categoryTitleToSlug(title: string) {
        if (!title) return '';
        return title.trim().replace(/\s+/g, '-');
    }

    categoryPathToQueryValue(categoryPath: string[], format: CategoryQueryFormat = this.categoryQueryFormat) {
        return categoryPathToQueryValueWithFormat(categoryPath, format);
    }

    setCategoryQueryFormatFromParam(queryParam?: string | null) {
        const value = queryParam?.trim();
        if (value) this.categoryQueryFormat = detectCategoryQueryFormat(value);
      }

    categoryPathMatchesQuerySlug(categoryPath: string[], slug: string) {
        return categoryPathMatchesQuerySlug(categoryPath, slug);
    }

    setLastNavigatedCategoryPath(path: string[] | null) {
        this.lastNavigatedCategoryPath = path?.length ? [...path] : null;
    }

    buildCategoryPathFromItem(
        category: any,
        parentCategoryPath: string[] | null = null,
        page: 'homepage' | 'category' = 'homepage',
    ): string[] | null {
        const title = category?.title ?? category?.name;
        if (!title) return null;

        if (parentCategoryPath?.length) {
            return [...parentCategoryPath, title];
        }

        if (Array.isArray(category?.category_path) && category.category_path.length) {
            const path = [...category.category_path];
            if (path[path.length - 1] !== title) {
                path.push(title);
            }
            return path;
        }

        return [title];
    }

    collectCategoryPaths(categories: any[], parentPath: string[] = []): string[][] {
        const paths: string[][] = [];
        for (const cat of categories ?? []) {
            const title = cat?.title ?? cat?.name;
            if (!title) continue;
            const path = [...parentPath, title];
            paths.push(path);
            const children = cat.subcategory ?? cat.sub_categories ?? [];
            paths.push(...this.collectCategoryPaths(children, path));
        }
        return paths;
    }

    greedyParseHyphenJoinedSlug(fullSlug: string, categories: any[]): string[] | null {
        const resolved: string[] = [];
        let remaining = fullSlug;
        let level = categories;

        while (remaining) {
            const sorted = [...level].sort(
                (a, b) => this.categoryTitleToSlug(b.title).length - this.categoryTitleToSlug(a.title).length,
            );
            let matched = false;

            for (const cat of sorted) {
                const slug = this.categoryTitleToSlug(cat.title);
                if (remaining === slug) {
                    resolved.push(cat.title);
                    remaining = '';
                    matched = true;
                    break;
                }
                if (remaining.startsWith(slug + '-')) {
                    resolved.push(cat.title);
                    remaining = remaining.slice(slug.length + 1);
                    const children = cat.subcategory ?? cat.sub_categories ?? [];
                    level = children.length ? children : categories;
                    matched = true;
                    break;
                }
            }

            if (!matched) {
                if (resolved.length && remaining) {
                    resolved.push(remaining.replace(/-/g, ' ').trim());
                    remaining = '';
                    break;
                }
                return null;
            }
        }

        return resolved.length ? resolved : null;
    }

    resolveHyphenJoinedCategorySlug(fullSlug: string, extraSources: any[] = []): string[] | null {
        const pool = this.mergeCategoryLookupPool(extraSources);
        const parsed = parseHyphenJoinedCategorySlug(fullSlug, pool);
        return parsed.length ? parsed : null;
    }

    mergeCategoryLookupPool(extraSources: any[] = []): any[] {
        return [...this.custArticleCat, ...extraSources];
    }

    resolveHyphenJoinedCategorySlugFallback(fullSlug: string, categories: any[]): string[] | null {
        for (const cat of categories ?? []) {
            const parentSlug = this.categoryTitleToSlug(cat.title);
            if (fullSlug === parentSlug) {
                return [cat.title];
            }
            if (fullSlug.startsWith(parentSlug + '-')) {
                const childSlug = fullSlug.slice(parentSlug.length + 1);
                const children = cat.subcategory ?? cat.sub_categories ?? [];
                const child = children.find(c => this.categoryTitleToSlug(c.title) === childSlug);
                if (child) {
                    return [cat.title, child.title];
                }
                if (childSlug) {
                    return [cat.title, childSlug.replace(/-/g, ' ').trim()];
                }
            }
        }
        return null;
    }

    resolveCategoryPathFromSlugs(slugSegments: string[], extraSources: any[] = []): string[] | null {
        if (!slugSegments?.length) return null;

        const pools = [...this.custArticleCat, ...extraSources].filter(Boolean);
        const slugKey = slugSegments.join('/');

        for (const cat of pools) {
            const path = cat.category_path?.length
                ? cat.category_path
                : cat.title
                    ? [cat.title]
                    : null;
            if (!path?.length) continue;
            const slugs = path.map(t => this.categoryTitleToSlug(t));
            if (slugs.join('/') === slugKey) return path;
        }

        const resolved: string[] = [];
        let level = this.custArticleCat.length ? this.custArticleCat : pools;

        for (const slug of slugSegments) {
            const match = level.find(c => this.categoryTitleToSlug(c.title) === slug);
            if (!match) return null;
            resolved.push(match.title);
            level = match.subcategory ?? match.sub_categories ?? [];
        }

        return resolved.length === slugSegments.length ? resolved : null;
    }

    normalizeCategoryPath(categoryPath: string[] | null | undefined): string[] {
        return normalizeCategoryPath(categoryPath);
    }

    preserveCategoryPath(categoryPath: string[] | null | undefined): string[] {
        return preserveCategoryPathSegments(categoryPath);
    }

    toApiCategoryPath(categoryPath: string[] | null | undefined): string[] {
        return toApiCategoryPath(categoryPath, this.custArticleCat);
    }

    getLastNavigatedPathForSlug(slug: string): string[] | null {
        const path = this.lastNavigatedCategoryPath;
        return path?.length && this.categoryPathMatchesQuerySlug(path, slug) ? [...path] : null;
    }

    resolveExactCategoryPathFromSlug(slug: string, extraSources: any[] = []): string[] | null {
        const pool = this.mergeCategoryLookupPool(extraSources).filter(Boolean);
        for (const cat of pool) {
            const title = cat?.title ?? cat?.name;
            if (!title || !cat.category_path?.length) continue;
            const path = [...cat.category_path];
            if (path[path.length - 1] !== title) path.push(title);
            if (this.categoryPathMatchesQuerySlug(path, slug)) return path;
        }
        return (
            this.collectCategoryPaths(pool).find(
                (path) => this.categoryPathMatchesQuerySlug(path, slug),
            ) ?? null
        );
    }

    resolveCategoryPathFromQuery(segments: string[], extraSources: any[] = []): string[] | null {
        if (!segments?.length) return null;
        if (segments.some((segment) => segment.includes(' '))) {
            if (segments.length === 1) {
                return normalizeSingleSegmentPath(
                    segments[0],
                    this.mergeCategoryLookupPool(extraSources),
                );
            }

            return preserveCategoryPathSegments(segments);
        }

        if (segments.length > 1) {
            const resolved = this.resolveCategoryPathFromSlugs(segments, extraSources);
            if (resolved?.length) return resolved;
            return segments.map(s => s.replace(/-/g, ' ').trim());
        }

        const slug = segments[0];
        const pool = this.mergeCategoryLookupPool(extraSources);

        const resolved =
            this.getLastNavigatedPathForSlug(slug) ??
            this.resolveExactCategoryPathFromSlug(slug, extraSources) ??
            this.greedyParseHyphenJoinedSlug(slug, pool) ??
            this.resolveHyphenJoinedCategorySlug(slug, extraSources);

        return resolved?.length
            ? resolved
            : parseHyphenJoinedCategorySlug(slug, pool);
    }

    isLegacyCategoryQuery(param: string) {
        if (!param) return false;
        return param.includes('%');
    }

    categoryQueryToPath(queryParam: string, extraSources: any[] = []): string[] {
        const segments = this.getCategoryPath(queryParam);
        if (!segments?.length) return [];

        const resolved = this.resolveCategoryPathFromQuery(segments, extraSources);
        return resolved?.length ? preserveCategoryPathSegments(resolved) : [];
    }

    getArticleContext(): {
        article_slug: string;
        article_lang: string;
        category_id: string;
        category_path: string[] | null;
    } {
        let node = this._router.routerState.snapshot.root;
        let article_slug: string | null = null;
        let article_lang: string | null = null;
        let kb_lang: string | null = null;
        let category_id: string | null = null;
        let category_path: string[] | null = null;

        while (node) {
            article_slug = node.paramMap.get('articleSlug') || article_slug;
            article_lang = node.paramMap.get('articleLang') || article_lang;
            kb_lang = node.paramMap.get('lang') || kb_lang;
            category_id = node.paramMap.get('categoryId') || category_id;
            const catParam = node.queryParamMap.get('category');
            if (catParam) {
                this.setCategoryQueryFormatFromParam(catParam);
                category_path = this.categoryQueryToPath(catParam);
            }
            const categoriesParam = node.queryParamMap.get('categories');
            if (categoriesParam) {
                this.setCategoryQueryFormatFromParam(categoriesParam);
            }
            const articleLangParam = node.queryParamMap.get('article_lang');
            if (articleLangParam) {
                article_lang = articleLangParam;
            }
            node = node.firstChild;
        }

        return {
            article_slug: article_slug || '',
            article_lang: article_lang || kb_lang || this.kbSettings?.language || 'en',
            category_id: category_id || '',
            category_path: category_path,
        };
    }

    buildCategoryUrl(
        kb_slug: string,
        kb_lang: string,
        categoryPath: string[],
    ): [string[], { queryParams: Record<string, string> }] {
        return [
            ['/', kb_slug, kb_lang, 'category'],
            { queryParams: { categories: this.categoryPathToQueryValue(categoryPath) } },
        ];
    }

    buildArticleUrl(
        kb_slug: string,
        kb_lang: string,
        articleSlug: string,
        categoryPath: string[] | null,
    ): [string[], { queryParams?: Record<string, string> }] {
        const segments = ['/', kb_slug, kb_lang, 'article', articleSlug];
        if (categoryPath?.length) {
            return [segments, { queryParams: { category: this.categoryPathToQueryValue(categoryPath) } }];
        }
        return [segments, {}];
    }

    getCategoryPathFromParents(parentsNode: any) {
        const path: string[] = [];
        for (let curr = parentsNode; curr?.title || curr?.name; curr = curr.child) {
            path.push(curr.title || curr.name);
        }
        return path.length ? path : null;
    }

    extractCategoryPathFromArticle(item: any) {
        if (!item) return null;
        if (Array.isArray(item.category_path) && item.category_path.length > 0) {
            return preserveCategoryPathSegments(item.category_path);
        }
        const fromParents =
            this.getCategoryPathFromParents(item.parents) ??
            this.getCategoryPathFromParents(item.categories?.[0]?.parents);
        if (fromParents?.length) return fromParents;
        const firstCategory = item.categories?.[0];
        const label = firstCategory?.title || firstCategory?.name;
        return label ? [label] : null;
    }

    setUuid(uuid: string | null) {
        this.searchUuid = uuid;
    }

    setActiveCategory(categoryId: number) {
        const valueToStore = categoryId ?? 0;
        this.actCategory.next(valueToStore);
    }

    getAllCategories(forceRefresh = false): Observable<any> {
        const kbId = this.getActiveKnowledgeBase()?.id;
        if (!kbId) {
            return of({ success: false, data: [] });
        }
        if (forceRefresh || this.allCategoriesCachedKbId !== kbId || !this.allCategoriesRequest$) {
            this.allCategoriesCachedKbId = kbId;
            this.allCategoriesRequest$ = this._api
                .getData('all-categories/' + kbId)
                .pipe(shareReplay({ bufferSize: 1, refCount: true }));
        }
        return this.allCategoriesRequest$;
    }

    invalidateAllCategoriesCache() {
        this.allCategoriesRequest$ = this.allCategoriesCachedKbId = null;
    }

    getKbslugDetails(forceRefresh = false): Observable<KbSlugDetails | null> {
        if (this._api.isDomainBlocked()) {
            return of(null);
        }

        if (forceRefresh || !this.kbSlugDetailsRequest$) {
            this.kbSlugDetailsRequest$ = this._api.getDomainConfig().pipe(
                switchMap(() => {
                    if (this._api.isDomainBlocked()) {
                        return EMPTY;
                    }
                    return this._api.getData('kb-data');
                }),
                tap((res: any) => {
                    if (res) {
                        this.kbSlugDetailsSubject.next(res);
                    }
                }),
                catchError(() => {
                    this.kbSlugDetailsRequest$ = null;
                    return of(null);
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            ) as Observable<KbSlugDetails | null>;
        }

        return this.kbSlugDetailsRequest$ ?? of(null);
    }

    getKbSettingsData(slug?: string) {
        this.getKbslugDetails()
            .pipe(
                take(1),
                catchError(() => of(null))
            )
            .subscribe((details) => {
                const resolvedSlug =
                    slug ||
                    this.getActiveKnowledgeBase()?.kb_slug ||
                    this.getPortalContext()?.kb_slug ||
                    details?.default_kb_slug;

                if (!resolvedSlug) {
                    return;
                }

                this.loadKbPageBuilder(resolvedSlug);
            });
    }

    loadKbPageBuilder(slug: string) {
        if (
            this.portalSsrHydratedSlug === slug &&
            this.isLoadKbSetting.getValue() &&
            this.kbSettings?.kb_slug === slug
        ) {
            return;
        }

        this.settingsLoadCancel$.next();
        this._api.getData(`kb-page-builder/${slug}`)
            .pipe(takeUntil(this.settingsLoadCancel$))
            .subscribe({
                next: (res: any) => {
                    // getData swallows HTTP 404 as null — treat as missing KB
                    if (!res) {
                        this.redirectToDefaultKb();
                        return;
                    }
                    this.applyKbPageBuilderResponse(res);
                },
                error: (err) => this.handleKbPageBuilderError(err),
            });
    }

    applyKbPageBuilderResponse(res: any): void {
        if (!res) {
            return;
        }

        if (
            isBrowser() &&
            res.livechat_settings?.status == 1 &&
            !readLocalStorage('token')
        ) {
            window.lc_id = res.livechat_settings.license_id;
            window.lc_dc = res.docs_domain;

            const chatWidget = document.createElement('app-chat-box');
            chatWidget.setAttribute('id', 'widget');
            document.body.insertAdjacentElement('beforeend', chatWidget);

            const deskuInstall = document.createElement('script');
            deskuInstall.src = ['dev', 'local'].includes(environment.domainType)
                ? 'https://widget.desku.dev/chat-widget.js?v=' + res.widget_version
                : 'https://widget.desku.io/chat-widget.js?v=' + res.widget_version;
            deskuInstall.setAttribute('defer', 'true');
            document.body.insertAdjacentElement('beforeend', deskuInstall);
        }

        this.kbSettings = res;
        this.darkerColorCache.clear();
        this.textColorCache.clear();
        this.collectionStylesCache.clear();

        if (this.custArticleCat.length > 0) {
            this.kbSettings.articleCategory = this.custArticleCat;
        }

        if (this.featured_articles.length > 0) {
            this.kbSettings.featured_articles = this.featured_articles;
        }

        this.parseJsonField('header_links', []);
        this.parseJsonField('footer_links', []);
        this.parseJsonField('footer_social_media_btns', []);

        this.socialMediaLink = this.kbSettings.footer_social_media_btns;

        if (this.kbSettings.se_body_bg_clr) {
            this.updateCategoryCardBg();
        }

        this.setRootCssVariable('--kb-action-color', this.kbSettings.se_action_clr);
        this.setHeroBackground();
        this.isLoadKbSetting.next(true);
    }

    applyPortalCategoriesResponse(res: any): void {
        if (!res) {
            return;
        }

        if (!res.success && this.isKbLangValidationError(res.errors)) {
            return;
        }

        const categories = res.success ? (res.data ?? []) : [];
        const featuredArticles = res.success ? (res.featured_articles ?? []) : [];

        this.custArticleCat = categories;
        this.featured_articles = featuredArticles;
        this.kbSettings.articleCategory = categories;
        this.kbSettings.featured_articles = featuredArticles;
        this.isCategoryLoaded.next(true);
    }

    hydrateFromPortalSsrState(state: {
        kbSlugDetails?: KbSlugDetails | null;
        kbSettings?: any | null;
        categoriesResponse?: any | null;
        slug?: string | null;
        tenantName?: string | null;
    }): void {
        if (state.tenantName) {
            this._api.setBaseUrl(state.tenantName);
            this._api.tenantHostname = state.tenantName;
        }

        if (state.kbSlugDetails) {
            this.kbSlugDetailsSubject.next(state.kbSlugDetails);
            this.kbSlugDetailsRequest$ = of(state.kbSlugDetails).pipe(
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }

        if (state.kbSettings) {
            this.applyKbPageBuilderResponse(state.kbSettings);
        }

        if (state.categoriesResponse) {
            this.applyPortalCategoriesResponse(state.categoriesResponse);
        }

        if (state.slug) {
            this.portalSsrHydratedSlug = state.slug;
        }
    }

    handleKbPageBuilderError(err: any) {
        const status = (err as { status?: number })?.status;

        // Missing / unknown KB slug → default KB home (e.g. /en → /knowledgebase/en)
        if (status === 404) {
            this.redirectToDefaultKb();
            return;
        }

        if (status === 410) {
            this.navigateTo404(410);
            return;
        }

        this.redirectToDefaultKb();
    }

    navigateTo404(code: 404 | 410 = 404) {
        this._api.navigateToKnowledgebaseError(code);
    }

    getValidKbLangCodes(): string[] {
        const defaultLang = this.kbSettings?.language;
        const otherLangs = (this.kbSettings?.other_languages ?? []).map(
            (l: KbLanguage) => l.code,
        );
        return [...new Set([defaultLang, ...otherLangs].filter(Boolean))];
    }

    isValidKbLang(lang: string | null | undefined) {
        if (!lang || lang.length > 10) {
            return false;
        }
        const validCodes = this.getValidKbLangCodes();
        if (!validCodes.length) {
            return true;
        }
        return validCodes.includes(lang);
    }

    isKbLangValidationError(err: any) {
        return !!err?.kb_lang;
    }

    isNotFoundResponse(res: any) {
        if (!res) return true;
        if (res.success === false) return true;
        return false;
    }

    redirectToDefaultKb(clearQueryParams: boolean = true): void {
        if (!isBrowser()) {
            return;
        }

        const url = this._router.url.split('?')[0].split('#')[0];
        const isNonKbRoute =
            url.startsWith('/tickets') ||
            url.startsWith('/customer-portal/tickets') ||
            /\/(customer-portal\/)?[^/]+\/show(\/|$)/.test(url) ||
            url.startsWith('/404') ||
            url.startsWith('/500');
        if (isNonKbRoute) return;

        const details = this.kbSlugDetailsSubject.getValue() as any;
        const detailsPayload = details?.data ?? details;

        // Prefer kb-data defaults — do not reuse the failed route slug (e.g. "en")
        const defaultSlug =
            detailsPayload?.default_kb_slug || this.kbSettings?.kb_slug;
        const defaultLanguage =
            detailsPayload?.default_language || this.kbSettings?.language;
        if (!defaultSlug || !defaultLanguage) return;

        // /en/article/... with invalid slug → /knowledgebase/en/article/...
        const legacyArticlePath = buildLegacyMissingKbSlugRedirectPath(
            this._router.url,
            defaultSlug,
        );
        if (legacyArticlePath) {
            void this._router.navigateByUrl(legacyArticlePath, { replaceUrl: true });
            return;
        }

        const targetPath = `/${defaultSlug}/${defaultLanguage}`;
        const currentPath = this._router.url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
        if (currentPath === targetPath) return;

        void this.navigateToKb(defaultSlug, defaultLanguage, clearQueryParams);
    }

    navigateToKb(slug?: string, language?: string, clearQueryParams: boolean = true): Promise<boolean> {
        if (!isBrowser()) {
            return Promise.resolve(false);
        }

        if (clearQueryParams) {
            return this._router.navigate(['/', slug, language], {
                queryParams: {},
                replaceUrl: true,
            });
        }

        const urlTree = this._router.createUrlTree(
            ['/', slug, language],
            { queryParamsHandling: 'preserve' }
        );

        if (this._router.isActive(urlTree, {
            paths: 'exact',
            queryParams: 'exact',
            fragment: 'ignored',
            matrixParams: 'ignored',
        })) {
            return Promise.resolve(true);
        }

        return this._router.navigateByUrl(urlTree, { replaceUrl: true });
    }

    parseJsonField(key: keyof typeof this.kbSettings, defaultValue: any = undefined) {
        const value = this.kbSettings?.[key];

        if (typeof value === 'string') {
            this.kbSettings[key] = JSON.parse(value);
        } else if (!value && defaultValue !== undefined) {
            this.kbSettings[key] = defaultValue;
        }
    }

    setHeroBackground() {
        if (!isBrowser()) {
            return;
        }

        switch (this.kbSettings.hero_bg) {
            case 'color':
                this.setRootCssVariable('--kb-header-img', this.kbSettings.hero_bg_clr);
                break;

            case 'image':
                this.setRootCssVariable('--kb-header-img', `url(${this.kbSettings.hero_img_path})`);
                break;

            case 'gradient':
                this.getGradientString('hero', this.kbSettings);
                break;
        }
    }

    getBackgroundImage(type, settings: any) {
        if (type !== 'hero') return 'none';

        const heroBg = settings?.hero_bg?.toLowerCase();

        if (heroBg === 'image' && settings?.hero_img) {
            this.setRootCssVariable('--kb-header-img', `url(${settings.hero_img_path})`);
            if (isBrowser()) {
                window.dispatchEvent(new Event('resize'));
            }
        }

        if (heroBg === 'gradient') {
            return this.getGradientString(type, settings);
        }

        if (heroBg === 'color' || (heroBg === 'image' && !settings?.hero_img)) {
            this.setRootCssVariable('--kb-header-img', settings?.hero_bg_clr || 'var(--desku-primary)');
        }

        return 'none';
    }

    getGradientString(type: string, settings: any, article?) {
        let colors: string[] = [];
        let position = 'to bottom';

        switch (type) {
            case 'hero':
                colors = [
                    settings?.hero_gradient_clr_1,
                    settings?.hero_gradient_clr_2,
                    settings?.hero_gradient_clr_3,
                ].filter(Boolean);
                position = settings?.hero_gradient_position || position;
                break;

            case 'collection':
                colors = [
                    article?.icon_gradient_bg_clr_1,
                    article?.icon_gradient_bg_clr_2,
                    article?.icon_gradient_bg_clr_3,
                ].filter(Boolean);
                position = article?.icon_gradient_position || position;
                break;

            case 'collectionBg':
                colors = [
                    settings?.cs_gradient_clr_1,
                    settings?.cs_gradient_clr_2,
                    settings?.cs_gradient_clr_3,
                ].filter(Boolean);
                position = settings?.cs_gradient_position || position;
                break;

            case 'contents':
                colors = [
                    settings?.contents_gradient_clr_1,
                    settings?.contents_gradient_clr_2,
                    settings?.contents_gradient_clr_3,
                ].filter(Boolean);
                position = settings?.contents_gradient_position || position;
                break;

            default:
                return 'none';
        }

        if (!colors.length) return 'none';

        const gradientString = `linear-gradient(${position}, ${colors.join(', ')})`;

        if (type === 'hero') {
            this.setRootCssVariable('--kb-header-img', gradientString);
            return isBrowser() ? 'var(--kb-header-img)' : gradientString;
        }

        return gradientString;
    }

    getHeroStyles(isCustomer: boolean = false) {
        const currentPath = this.getCurrentPath();
        const isCustomerPortal = isCustomer;
        const isFadeBg = !!this.kbSettings?.hero_is_fade_bg;
        const isContentBg = !!this.kbSettings?.cs_is_bg;
        const heroBg = this.kbSettings?.hero_bg?.toLowerCase();
        const hasHeroImage = heroBg === 'image' && !!this.kbSettings?.hero_img;
        const isArticleOrCategory =
            currentPath.includes('category') || currentPath.includes('article');

        if (isCustomerPortal) {
            this.activeTheme = 'Homepage';
        }

        const isHomepageTheme = this.activeTheme?.toLowerCase() === 'homepage';

        const applyFadeLayout =
            isFadeBg && !isArticleOrCategory && (isHomepageTheme || isCustomerPortal);

        const applyFadeBackground =
            isFadeBg && isHomepageTheme && !isArticleOrCategory;

        const bgColor = this.hexToRgb(this.kbSettings?.se_body_bg_clr || '#FFFFFF');
        const fadeLayer = `radial-gradient(333.38% 100% at 50% 0%, ${this.generateGradientStops(bgColor)}), var(--kb-header-img)`;

        const imageStyles = {
            'background-image': 'var(--kb-header-img)',
            'background-size': 'cover',
            'background-position': 'center',
            'background-repeat': 'no-repeat',
        };

        const scale = !isCustomer ? 0.87 : 1;
        const baseStyles = {
            'min-height': `${(this.kbSettings?.hero_height * scale) || (245 * scale)}px`,
            'padding-bottom': applyFadeLayout
                ? (isCustomerPortal ? '110px' : '115px')
                : '0px',
            'margin-bottom': applyFadeLayout
                ? (isContentBg
                    ? (isCustomerPortal ? '-123px' : '-131px')
                    : '-125px')
                : '0px',
        };

        if (applyFadeBackground) {
            if (!hasHeroImage) {
                return {
                    ...baseStyles,
                    background: fadeLayer,
                };
            }

            return {
                ...baseStyles,
                'background-image': fadeLayer,
                'background-size': 'cover',
                'background-position': 'center',
                'background-repeat': 'no-repeat',
            };
        }

        return {
            ...baseStyles,
            ...(heroBg === 'color' ||
                heroBg === 'gradient' ||
                (heroBg === 'image' && !this.kbSettings?.hero_img)
                ? { background: 'var(--kb-header-img)' }
                : imageStyles),
        };
    }

    private getCurrentPath(): string {
        if (this._request?.url) {
            return this._request.url.split('?')[0];
        }

        if (isBrowser()) {
            return window.location.pathname;
        }

        return '';
    }

    private setRootCssVariable(name: string, value: string): void {
        if (!isBrowser()) {
            return;
        }

        document.documentElement.style.setProperty(name, value);
    }

    hexToRgb(hex: string | undefined): { r: number; g: number; b: number } {
        hex = hex || '#FFFFFF';
        hex = hex.replace(/^#/, '');
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;

        return { r, g, b };
    }

    getCollectionStyles(article?) {
        const cacheKey = this.buildCollectionStylesCacheKey(article);
        if (this.collectionStylesCache.has(cacheKey)) {
            return this.collectionStylesCache.get(cacheKey);
        }

        const imageStyle = (url: string) => ({
            'background-image': `url(${url})`,
            'background-size': 'cover',
            'background-position': 'center',
            'background-repeat': 'no-repeat',
        });

        let result: Record<string, string> | undefined;
        if (this.kbSettings.cs_design?.toLowerCase() === 'icon') {
            switch (article?.icon_bg) {
                case 'color':
                case 'image':
                    result = { 'background-color': article?.icon_bg_clr || '#ffffff', };
                    break;

                case 'gradient':
                    result = { 'background-image': this.getGradientString('collection', this.kbSettings, article) };
                    break;
            }

        } else if (this.kbSettings.cs_design === 'image') {
            if (article?.imageUrl) {
                result = imageStyle(article.imageUrl);
            } else {
                result = { 'background-color': 'transparent' };
            }
        }

        this.collectionStylesCache.set(cacheKey, result);
        return result;
    }

    buildCollectionStylesCacheKey(article?: any) {
        const settings = this.kbSettings || {};
        const item = article || {};
        return [
            item.id ?? '',
            settings.cs_design ?? '',
            item.icon_bg ?? '',
            item.icon_bg_clr ?? '',
            item.icon_gradient_bg_clr_1 ?? '',
            item.icon_gradient_bg_clr_2 ?? '',
            item.icon_gradient_bg_clr_3 ?? '',
            item.icon_gradient_position ?? '',
            item.imageUrl ?? '',
        ].join('|');
    }

    collectionMainDivStyle() {
        const borderRadius = `${this.kbSettings.se_radius || 0}px`;

        if (!this.kbSettings.cs_is_bg) {
            return;
        }

        switch (this.kbSettings.cs_bg) {
            case 'color':
                return {
                    'background-color': this.kbSettings.cs_bg_clr || '#F5F5F5',
                    'border-radius': borderRadius,
                };

            case 'image':
                if (this.kbSettings.cs_img) {
                    return {
                        'background-image': `url(${this.kbSettings.cs_img})`,
                        'background-size': 'cover',
                        'background-position': 'center',
                        'background-repeat': 'no-repeat',
                        'border-radius': borderRadius,
                    };
                }

                return {
                    'background-color': this.kbSettings.cs_bg_clr || '#F5F5F5',
                    'border-radius': borderRadius,
                };

            case 'gradient':
                return {
                    'background-image': this.getGradientString(
                        'collectionBg',
                        this.kbSettings
                    ),
                    'border-radius': borderRadius,
                };
        }
    }

    getContentsSectionStyle() {
        const isCardDesign = this.kbSettings.contents_design?.toLowerCase() === 'card';

        const style: any = {};

        if (
            this.kbSettings.contents_bg === 'color' ||
            (this.kbSettings.contents_bg === 'image' &&
                !this.kbSettings.contents_img)
        ) {
            style['background-color'] =
                this.kbSettings.contents_bg_clr || '#F5F5F5';
        } else if (
            this.kbSettings.contents_bg === 'image' &&
            this.kbSettings.contents_img_path
        ) {
            style['background-image'] = `url(${this.kbSettings.contents_img_path})`;
            style['background-size'] = 'cover';
            style['background-position'] = 'center';
            style['background-repeat'] = 'no-repeat';
        } else if (this.kbSettings.contents_bg === 'gradient') {
            style['background-image'] = this.getGradientString(
                'contents',
                this.kbSettings
            );
        } else {
            return;
        }

        if (isCardDesign) {
            style['border-radius'] = `${this.kbSettings.se_radius || 0}px`;
        }

        return style;
    }

    getDarkerColor(color: string, factor: number = 0.3, type?): string {
        const cacheKey = `${this.kbSettings?.cs_style || ''}|${this.kbSettings?.cs_design || ''}|${color || ''}|${factor}|${type ?? ''}`;
        const cached = this.darkerColorCache.get(cacheKey);
        if (cached !== undefined) {
            return cached;
        }

        let result: string;
        if (
            this.kbSettings.cs_style == 'visual' &&
            this.kbSettings.cs_design == 'imagess' &&
            !type
        ) {
            result = '1px solidtransparent';
        } else {
            if (color == '#fff' || color == '#ffffff') {
                result = '1px solid #e5e5e5';
            } else {
                result = this.generateBorderColor(color, type);
            }
        }

        this.darkerColorCache.set(cacheKey, result);
        return result;
    }

    generateBorderColor(color, type?) {
        if (!color) return '1px solid #e5e5e5';

        let r: number;
        let g: number;
        let b: number;

        if (color.startsWith('rgb')) {
            const rgbValues = color.match(/\d+/g);

            if (!rgbValues || rgbValues.length !== 3) {
                throw new Error(
                    "Invalid RGB format. Please provide in 'rgb(r, g, b)' format."
                );
            }

            [r, g, b] = rgbValues.map(Number);
        } else if (color.startsWith('#')) {
            const hex = color.slice(1);

            if (hex.length !== 6) {
                throw new Error(
                    "Invalid HEX format. Please provide in '#RRGGBB' format."
                );
            }

            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        } else {
            return '1px solid #e5e5e5';
        }

        if (![r, g, b].every(Number.isFinite)) {
            return type === 'variable'
                ? '#e5e5e5'
                : '1px solid #e5e5e5';
        }

        const [h, s, lightness] = this.rgbToHsl(r, g, b);

        const adjustedLightness =
            lightness < 0.5
                ? Math.min(lightness + 0.3, 0.9)
                : Math.max(lightness - 0.1, 0.1);

        const [newR, newG, newB] = this.hslToRgb(h, s, adjustedLightness);

        const borderColor = `#${newR.toString(16).padStart(2, '0')}${newG
            .toString(16)
            .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;

        return type === 'variable'
            ? borderColor
            : `1px solid ${borderColor}`;

    }

    // Helper function to convert RGB to HSL
    rgbToHsl(r, g, b) {
        r /= 255;
        g /= 255;
        b /= 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h,
            s,
            l = (max + min) / 2;

        if (max === min) {
            h = s = 0; // achromatic
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }

        return [h, s, l];
    }

    hslToRgb(h, s, l) {
        let r, g, b;

        if (s === 0) {
            r = g = b = l; // achromatic
        } else {
            const hue2rgb = (p, q, t) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    }

    anyArticleHasDescription(): boolean {
        return this.kbSettings?.articleCategory?.some(
            (article) => article && article?.id && article?.description
        );
    }

    getKbArticlesCategory(data: any): void {
        this.articlesCategoryLoadCancel$.next();
        this._networkService.getKbArticlesCategory(data)
            .pipe(takeUntil(this.articlesCategoryLoadCancel$))
            .subscribe({
                next: (res: any) => {
                    if (!res) {
                        return;
                    }

                    if (!res.success && this.isKbLangValidationError(res.errors)) {
                        this.navigateTo404();
                        return;
                    }

                    this.applyPortalCategoriesResponse(res);
                },
                error: (err) => {
                    const status = err?.status as number | undefined;
                    if (status === 404 || status === 410) {
                        this.navigateTo404(status === 410 ? 410 : 404);
                        return;
                    }
                    if (this.isKbLangValidationError(err) || !this.isValidKbLang(data.kb_lang)) {
                        this.navigateTo404();
                        return;
                    }
                    this.redirectToDefaultKb();
                },
            });
    }

    getArticles() {
        const data = {
            all: '1',
        };
        this.articlesLoadCancel$.next();
        this._networkService.postKbArticles(data)
            .pipe(takeUntil(this.articlesLoadCancel$))
            .subscribe({
                next: (res) => {
                    if (res.success) {
                        if (res.data) {
                            this.allArticles = res.data;
                            this.isArticlesLoaded.next(true);
                        }
                    } else {
                        this._util.customErrorToast(res.message);
                    }
                },
            });
    }

    getImage(e, field, isDrop?) {
        var reader = new FileReader();
        if (!isDrop) {
            reader.readAsDataURL(e.target.files[0]); // read file as data url
        } else {
            reader.readAsDataURL(e.addedFiles[0]); // read file as data url
        }

        reader.onload = (event: any) => {
            this.kbSettings[field] = event.target.result;
            this.kbSettings[field + '_path'] = event.target.result;
            if (this.getImageTimerId != null) {
                clearTimeout(this.getImageTimerId);
                this.getImageTimerId = null;
            }
            this.getImageTimerId = setTimeout(() => {
                this.getImageTimerId = null;
                if (field == 'hero_img') {
                    this.getBackgroundImage('hero', this.kbSettings);
                }
            }, 1000);

        };
    }

    getTextColorBasedOnBackground(bgColor: string, type?: string): string {
        if (!bgColor || typeof bgColor !== 'string') return 'black';

        if (type == 'color') {
            bgColor = bgColor.replace('#', '');
            if (bgColor.length !== 6) return 'black';

            const r = parseInt(bgColor.slice(0, 2), 16);
            const g = parseInt(bgColor.slice(2, 4), 16);
            const b = parseInt(bgColor.slice(4, 6), 16);

            if (isNaN(r) || isNaN(g) || isNaN(b)) return 'black';

            const isLightBg = ((0.299 * r + 0.587 * g + 0.114 * b) / 255) > 0.5;
            this.setRootCssVariable('--kb-primary-text', isLightBg ? '#0b0d13' : '#ffffff');
            this.setRootCssVariable('--kb-secondary-text', isLightBg ? '#333333' : '#e1e1e1');
            this.setRootCssVariable('--kb-info-text', isLightBg ? '#7d7f86' : '#a0a0a0');
            return isLightBg ? '#000000' : '#FFFFFF';
        }

        const cacheKey = `${bgColor}|${type ?? ''}`;
        const cached = this.textColorCache.get(cacheKey);
        if (cached !== undefined) {
            return cached;
        }

        const normalized = bgColor.replace('#', '');
        if (normalized.length !== 6) return 'black';

        const r = parseInt(normalized.slice(0, 2), 16);
        const g = parseInt(normalized.slice(2, 4), 16);
        const b = parseInt(normalized.slice(4, 6), 16);

        if (isNaN(r) || isNaN(g) || isNaN(b)) return 'black';

        const isLightBg = ((0.299 * r + 0.587 * g + 0.114 * b) / 255) > 0.5;
        const result = isLightBg ? 'dark' : 'light';
        this.textColorCache.set(cacheKey, result);
        return result;
    }

    setCategoryColor() {
        let color = this.kbSettings.cs_is_bg
            ? this.kbSettings.cs_bg_clr
            : this.kbSettings.se_body_bg_clr;

        if (!color || typeof color !== 'string') return 'black';

        color = color.replace('#', '');

        if (color.length !== 6) return 'black';

        const r = parseInt(color.slice(0, 2), 16);
        const g = parseInt(color.slice(2, 4), 16);
        const b = parseInt(color.slice(4, 6), 16);

        if (isNaN(r) || isNaN(g) || isNaN(b)) return 'black';

        const isLightBg = ((0.299 * r + 0.587 * g + 0.114 * b) / 255) > 0.5;

        this.setRootCssVariable('--kb-category-primary-text', isLightBg ? '#0b0d13' : '#ffffff');
        this.setRootCssVariable('--kb-category-secondary-text', isLightBg ? '#333333' : '#e1e1e1');
        this.setRootCssVariable('--kb-category-info-text', isLightBg ? '#7d7f86' : '#a0a0a0');
    }

    updatePage(event) {
        this.activeTheme = event.value;
    }

    generateGradientStops({ r, g, b }) {
        return [
            0, 11.67, 21.17, 28.85, 35.03, 40.05, 44.25, 47.96, 51.51, 55.23,
            59.47, 64.55, 70.81, 78.58, 88.2, 100,
        ]
            .map(
                (stop, index) =>
                    `rgba(${r}, ${g}, ${b}, ${index === 0
                        ? 0
                        : index === 15
                            ? 1
                            : (index * 0.0666).toFixed(8)
                    }) ${stop}%`
            )
            .join(', ');
    }

    getSearchBarBgColor() {
        const settings = this.kbSettings;
        if (settings?.hero_bg?.toLowerCase() == 'gradient') {
            return this.getGradientOverlay(settings);
        } else if (
            settings?.hero_bg?.toLowerCase() == 'image' &&
            settings?.hero_img
        ) {
            return this.getOverlayWithDynamicOpacity(
                settings?.hero_bg_clr || 'var(--desku-primary)'
            );
        } else {
            return this.getOverlayWithDynamicOpacity(
                settings?.hero_bg_clr || 'var(--desku-primary)'
            );
        }
    }

    getGradientOverlay(settings: any): string {
        const colors = [
            settings?.hero_gradient_clr_1,
            settings?.hero_gradient_clr_2,
            settings?.hero_gradient_clr_3,
        ].filter(Boolean); // Remove undefined or null values

        if (colors.length === 0) {
            return this.getOverlayWithDynamicOpacity('#FFFFFF'); // Fallback if no colors
        }

        return this.getOverlayWithDynamicOpacity(colors[0]);
    }

    getOverlayWithDynamicOpacity(color: string): string {
        const adjustedColor = this.adjustColorBrightness(color, 30);
        const isLightColor = this.isLightColor(adjustedColor);
        const opacity = isLightColor ? 0.2 : 0.7;
        return `${adjustedColor}${Math.round(opacity * 255)
            .toString(16)
            .padStart(2, '0')}`;
    }

    adjustColorBrightness(color: string, amount: number): string {
        let usePound = false;
        if (color[0] == '#') {
            color = color.slice(1);
            usePound = true;
        }

        let num = parseInt(color, 16);
        let r = (num >> 16) + amount;
        let g = ((num >> 8) & 0x00ff) + amount;
        let b = (num & 0x0000ff) + amount;

        const newColor = (
            0x1000000 +
            (r < 255 ? (r < 1 ? 0 : r) : 255) * 0x10000 +
            (g < 255 ? (g < 1 ? 0 : g) : 255) * 0x100 +
            (b < 255 ? (b < 1 ? 0 : b) : 255)
        )
            .toString(16)
            .slice(1);

        return (usePound ? '#' : '') + newColor;
    }

    isLightColor(color: string): boolean {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 155; // You can adjust this threshold as needed
    }

    changeValue(event: any) {
        this.kbSettings.cs_is_bg = event ? '1' : '0';
        this.updateCategoryCardBg();
    }

    updateCategoryCardBg() {
        const color = this.kbSettings.se_body_bg_clr;
        const isCategoryBG = this.kbSettings.cs_is_bg;
        const isDarkBG = this.getTextColorBasedOnBackground(color);
        const isWhiteBg = color === '#fff' || color === '#ffffff';

        this.setRootCssVariable('--kb-bg-color', color);

        this.getTextColorBasedOnBackground(color, 'color');
        this.setCategoryColor();

        this.setRootCssVariable(
            '--kb-category-card-bg',
            isCategoryBG && this.kbSettings.cs_bg_clr
                ? '#ffffff'
                : isDarkBG === 'dark'
                    ? 'rgb(0,0,0,0.01)'
                    : 'rgb(255,255,255,0.10)'
        );

        if (isWhiteBg) {
            this.setRootCssVariable('--kb-rating-bg', '#F8F8FB');
            this.setRootCssVariable('--kb-card-bg', '#ffffff');
            this.setRootCssVariable('--kb-border-color', '#cccccc');
        } else {
            this.setRootCssVariable(
                '--kb-rating-bg',
                isDarkBG == 'dark' ? 'rgb(0,0,0,0.15)' : 'rgb(255,255,255,0.15)'
            );
            this.setRootCssVariable(
                '--kb-card-bg',
                isDarkBG == 'dark' ? 'rgb(0,0,0,0.01)' : 'rgb(255,255,255,0.10)'
            );
            this.setRootCssVariable(
                '--kb-border-color',
                this.generateBorderColor(color, 'variable')
            );
        }
    }

    hasArticleDesAdmin() {
        return this.articleCategory?.some(
            (article) => article && article?.id && article?.description
        );
    }
}
